import React, { useEffect, useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import client from '../../api/client';

// One-time host attestation that guests agreed to receive texts.
// `hideWhenAuthorized` turns it into a banner (Invitations page).
export const HostSmsAuthorization = ({ hideWhenAuthorized = false, onAuthorized }) => {
  const [state, setState] = useState(null);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    client.get('/compliance/host-authorization').then(({ data }) => setState(data)).catch(() => {});
  }, []);

  if (!state) return null;
  if (state.authorized && hideWhenAuthorized) return null;

  const confirm = async () => {
    setSaving(true); setError('');
    try {
      const { data } = await client.post('/compliance/host-authorization', { accept: true });
      setState((s) => ({ ...s, ...data }));
      onAuthorized?.();
    } catch (e) {
      setError(e.message || 'Could not save');
    } finally { setSaving(false); }
  };

  if (state.authorized) {
    return (
      <div data-testid="host-sms-auth-done" className="rounded-2xl p-4 bg-[#EAF2E5] border border-[#C9DDBB] flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-[#2F5D2F] flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-[#2F5D2F]">Guest texting authorized</p>
          <p className="text-xs text-[#4A6A4A]">Confirmed {new Date(state.at).toLocaleString()} · wording v{state.version}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="host-sms-auth-card" className="rounded-2xl p-5 bg-[#FBF1EA] border border-[#EFC9B0] space-y-3">
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-[#7B2C3B] flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-serif text-lg text-[#2A2A2A]">Before you text your guests</p>
          <p className="text-xs text-[#6A6A6A]">A one-time confirmation required by US texting rules. Guest texts stay paused until you confirm.</p>
        </div>
      </div>
      <label className="flex items-start gap-2.5 text-sm text-[#3A3A3A] cursor-pointer">
        <input data-testid="host-sms-auth-checkbox" type="checkbox" className="mt-1 accent-[#7B2C3B] w-4 h-4 flex-shrink-0" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        <span>{state.text}</span>
      </label>
      {error && <p data-testid="host-sms-auth-error" className="text-xs text-red-700">{error}</p>}
      <button data-testid="host-sms-auth-confirm" disabled={!checked || saving} onClick={confirm} className="text-sm px-4 py-2 rounded-full bg-[#7B2C3B] text-white disabled:opacity-40 inline-flex items-center gap-1.5 transition-opacity">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Confirm & enable guest texting
      </button>
    </div>
  );
};

export default HostSmsAuthorization;
